import React, { useState } from 'react';
import { isNil } from 'lodash';

import CardDialog from '~/components/CardDialog';

import type {
  ReportTemplateResponse,
  TemplateUploadResponse,
  UpdateReportTemplateRequest,
  UpdateSheetConfiguration,
} from './hooks/useReportTemplates';
import { useReportTemplates } from './hooks/useReportTemplates';
import ReportTemplateForm from './ReportTemplateForm';
import { REPORT_TEMPLATES_TEXTS } from './texts';
import type { FormValues, SheetFormValue, SheetsValidationState } from './types';
import { isConfiguredSheet } from './types';
import type { UploadResult } from './UploadReportFileStep';
import UploadReportFileStep from './UploadReportFileStep';

/** Build initial form values from template */
function buildInitialFormValues(template: ReportTemplateResponse): FormValues {
  return {
    templateName: template.name,
    sheets: (template.sheets ?? []).map((sheet) => ({
      id: sheet.id,
      sheet_name: sheet.sheet_name,
      display_name: sheet.display_name,
      metabase_question_id: sheet.metabase_question_id,
    })),
  };
}

/** Build initial validation state from template */
function buildInitialValidationState(template: ReportTemplateResponse): SheetsValidationState {
  const state: SheetsValidationState = {};
  for (const sheet of template.sheets ?? []) {
    state[sheet.sheet_name] = {
      matchedVariables: sheet.matched_variables ?? [],
      unmatchedVariables: sheet.unmatched_variables ?? [],
      isValidating: false,
      extracted_variables: sheet.extracted_variables ?? [],
    };
  }
  return state;
}

interface EditTemplateDialogProps {
  template: ReportTemplateResponse;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditTemplateDialog: React.FC<EditTemplateDialogProps> = ({ template, onClose, onSuccess }) => {
  const { updateTemplate, isSubmitting } = useReportTemplates();

  // Track whether we're showing the file upload step
  const [isReplacingFile, setIsReplacingFile] = useState(false);

  // Track if file has been replaced (to include gcs_path in request)
  const [replacementUploadData, setReplacementUploadData] = useState<TemplateUploadResponse | null>(null);

  const [formValues, setFormValues] = useState<FormValues>(() => buildInitialFormValues(template));
  const [sheetsValidationState, setSheetsValidationState] = useState<SheetsValidationState>(() =>
    buildInitialValidationState(template)
  );

  // Compute uploadedData - either from replacement or build from template
  const uploadedData: TemplateUploadResponse = replacementUploadData ?? {
    gcs_path: '', // Empty since we're using existing file
    filename: template.file_name,
    sheets: (template.sheets ?? []).map((sheet) => ({
      sheet_name: sheet.sheet_name,
      extracted_variables: sheet.extracted_variables ?? [],
    })),
  };

  const handleDeleteFile = () => {
    setIsReplacingFile(true);
    setFormValues((prev) => ({
      ...prev,
      sheets: prev.sheets.map((sheet) => ({
        ...sheet,
        metabase_question_id: null,
      })),
    }));
  };

  const handleUploadComplete = (data: UploadResult) => {
    setReplacementUploadData(data.uploadResponse);

    const existingSheetsByName: Record<string, SheetFormValue> = {};
    for (const sheet of formValues.sheets) {
      existingSheetsByName[sheet.sheet_name] = sheet;
    }

    const mergedSheets: SheetFormValue[] = data.initialFormValues.sheets.map((newSheet) => {
      const existing = existingSheetsByName[newSheet.sheet_name];
      if (existing) {
        // Sheet exists in both old and new file - preserve settings
        return {
          id: existing.id, // Keep ID for update
          sheet_name: newSheet.sheet_name,
          display_name: existing.display_name, // Preserve user's display name
          metabase_question_id: existing.metabase_question_id, // Preserve question selection
        };
      }
      // New sheet - use defaults from upload
      return {
        sheet_name: newSheet.sheet_name,
        display_name: newSheet.display_name,
        metabase_question_id: null,
      };
    });

    setFormValues({
      templateName: formValues.templateName,
      sheets: mergedSheets,
    });

    const mergedValidationState: SheetsValidationState = {};
    for (const sheet of mergedSheets) {
      const newValidation = data.sheetsValidationState[sheet.sheet_name];
      const existingValidation = sheetsValidationState[sheet.sheet_name];

      if (newValidation) {
        // Use new extracted_variables, but preserve matched/unmatched if sheet existed
        mergedValidationState[sheet.sheet_name] = {
          extracted_variables: newValidation.extracted_variables,
          matchedVariables: existingValidation?.matchedVariables ?? [],
          unmatchedVariables: existingValidation?.unmatchedVariables ?? [],
          isValidating: false,
        };
      }
    }
    setSheetsValidationState(mergedValidationState);

    setIsReplacingFile(false);
  };

  const handleCancelReplaceFile = () => {
    setIsReplacingFile(false);
  };

  const handleFormSubmit = async (values: FormValues) => {
    // Filter sheets that have been configured using type guard
    const configuredSheets: UpdateSheetConfiguration[] = values.sheets.filter(isConfiguredSheet).map((sheet) => ({
      id: sheet.id, // Will be undefined for new sheets
      sheet_name: sheet.sheet_name,
      display_name: sheet.display_name,
      metabase_question_id: sheet.metabase_question_id,
    }));

    const request: UpdateReportTemplateRequest = {
      name: values.templateName,
      sheets: configuredSheets,
    };

    // Include gcs_path only if file was replaced
    if (replacementUploadData) {
      request.gcs_path = replacementUploadData.gcs_path;
    }

    if (isNil(template.id)) {
      throw new Error('Template ID is required for update');
    }
    const result = await updateTemplate(template.id, request);
    if (result) {
      onSuccess?.();
      onClose();
    }
  };

  return (
    <CardDialog
      isDialog
      title={REPORT_TEMPLATES_TEXTS.EDIT_DIALOG_TITLE}
      onClose={onClose}
      preventClose={isSubmitting}
      maxWidth="sm"
      fullWidth
    >
      <div className="flex flex-col gap-16">
        {isReplacingFile ? (
          <UploadReportFileStep onUploadComplete={handleUploadComplete} onCancel={handleCancelReplaceFile} />
        ) : (
          <ReportTemplateForm
            mode="edit"
            initialValues={formValues}
            uploadedData={uploadedData}
            initialSheetsValidationState={sheetsValidationState}
            onSubmit={handleFormSubmit}
            onCancel={onClose}
            onDeleteFile={handleDeleteFile}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    </CardDialog>
  );
};

export default EditTemplateDialog;
